import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

/**
 * Export data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the file (without extension)
 * @param {String} sheetName - Name of the Excel sheet
 * @param {Object} options - Additional options for export
 */
export const exportToExcel = (
  data,
  filename = "export",
  sheetName = "Sheet1",
  options = {}
) => {
  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Convert data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(data, {
      header: options.headers || undefined,
      dateNF: options.dateFormat || "mm/dd/yyyy",
    });

    // Set column widths if provided
    if (options.columnWidths) {
      worksheet["!cols"] = options.columnWidths;
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
      bookSST: false,
    });

    // Save file
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `${filename}_${new Date().toISOString().split("T")[0]}.xlsx`);

    return { success: true, message: "Excel file downloaded successfully" };
  } catch (error) {
    console.error("Excel export error:", error);
    return { success: false, message: "Failed to export Excel file", error };
  }
};

/**
 * Format panel data for Excel export
 */
export const formatPanelsForExport = (panels) => {
  return panels.map((panel) => ({
    "Panel ID": panel.id,
    Title: panel.title,
    Status: panel.status,
    Visibility: panel.visibility,
    "Created Date": panel.createdAt
      ? new Date(panel.createdAt).toLocaleDateString()
      : "N/A",
    Deadline: panel.deadline
      ? new Date(panel.deadline).toLocaleDateString()
      : "N/A",
    "Published Date": panel.publishedDate
      ? new Date(panel.publishedDate).toLocaleDateString()
      : "N/A",
    "Completed Date": panel.completedDate
      ? new Date(panel.completedDate).toLocaleDateString()
      : "N/A",
    Progress: `${panel.progress || 0}%`,
    "Panel Admin": panel.admin?.name || "Not Assigned",
    "Members Count": panel.members?.length || panel.panelUsers?.length || 0,
    "Documents Count": panel.documents?.length || 0,
  }));
};

/**
 * Format applications data for Excel export
 */
export const formatApplicationsForExport = (applications) => {
  return applications.map((app) => ({
    "Application ID": app.id || app.applicationId,
    "Applicant Name": app.applicantName,
    Email: app.email,
    Phone: app.phone || "N/A",
    Status: app.status,
    "Expertise Areas": Array.isArray(app.expertise)
      ? app.expertise.join(", ")
      : app.expertise || "N/A",
    "Years of Experience": app.yearsExperience || 0,
    Education: app.education || "N/A",
    Certifications: Array.isArray(app.certifications)
      ? app.certifications.join(", ")
      : app.certifications || "N/A",
    "Submitted Date": app.submittedDate
      ? new Date(app.submittedDate).toLocaleDateString()
      : "N/A",
    "Reviewed Date": app.reviewedDate
      ? new Date(app.reviewedDate).toLocaleDateString()
      : "N/A",
    "Approved Date": app.approvedDate
      ? new Date(app.approvedDate).toLocaleDateString()
      : "N/A",
    "Rejected Date": app.rejectedDate
      ? new Date(app.rejectedDate).toLocaleDateString()
      : "N/A",
    "Rejection Reason": app.rejectionReason || "N/A",
    Bio: app.bio || app.coverLetter || "N/A",
  }));
};

/**
 * Format cadre members data for Excel export
 */
export const formatCadreMembersForExport = (members) => {
  return members.map((member) => ({
    "Member ID": member.id,
    Name: member.user?.name || member.User?.name || "N/A",
    Email: member.user?.email || member.User?.email || "N/A",
    Phone: member.user?.phone || member.User?.phone || "N/A",
    Status: member.user?.status || member.User?.status || "ACTIVE",
    Expertise: Array.isArray(member.expertise)
      ? member.expertise.join(", ")
      : member.expertise || "N/A",
    "Years Experience": member.yearsExperience || 0,
    Education: member.education || "N/A",
    Certifications: Array.isArray(member.certifications)
      ? member.certifications.join(", ")
      : member.certifications || "N/A",
    Rating: member.rating || 0,
    "Panels Completed": member.panelsCompleted || 0,
    Availability: member.availability || "N/A",
    Region: member.region || "N/A",
    "Is Panel Admin": member.isPanelAdmin ? "Yes" : "No",
    "Admin For Panels": Array.isArray(member.adminForPanels)
      ? member.adminForPanels.length
      : 0,
    "Joined Date": member.joinedDate
      ? new Date(member.joinedDate).toLocaleDateString()
      : "N/A",
    "COI Status":
      member.conflictOfInterestDetail?.evaluatedStatus ||
      member.panelConflictOfInterest?.evaluatedStatus ||
      "NOT_ANSWERED",
  }));
};

/**
 * Export multiple sheets to a single Excel file
 */
export const exportMultipleSheets = (sheets, filename = "export") => {
  try {
    const workbook = XLSX.utils.book_new();

    sheets.forEach(({ data, sheetName, options = {} }) => {
      const worksheet = XLSX.utils.json_to_sheet(data, {
        header: options.headers || undefined,
        dateNF: options.dateFormat || "mm/dd/yyyy",
      });

      if (options.columnWidths) {
        worksheet["!cols"] = options.columnWidths;
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
      bookSST: false,
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `${filename}_${new Date().toISOString().split("T")[0]}.xlsx`);

    return { success: true, message: "Excel file downloaded successfully" };
  } catch (error) {
    console.error("Excel export error:", error);
    return { success: false, message: "Failed to export Excel file", error };
  }
};

/**
 * Generate comprehensive admin report with all data
 */
export const exportAdminReport = (data) => {
  const sheets = [];

  // Add panels sheet if data exists
  if (data.panels && data.panels.length > 0) {
    sheets.push({
      data: formatPanelsForExport(data.panels),
      sheetName: "Panels",
      options: {
        columnWidths: [
          { wch: 10 }, // Panel ID
          { wch: 30 }, // Title
          { wch: 15 }, // Status
          { wch: 12 }, // Visibility
          { wch: 12 }, // Created Date
          { wch: 12 }, // Deadline
          { wch: 12 }, // Published Date
          { wch: 12 }, // Completed Date
          { wch: 10 }, // Progress
          { wch: 20 }, // Panel Admin
          { wch: 12 }, // Members Count
          { wch: 12 }, // Documents Count
        ],
      },
    });
  }

  // Add applications sheet if data exists
  if (data.applications && data.applications.length > 0) {
    sheets.push({
      data: formatApplicationsForExport(data.applications),
      sheetName: "Applications",
      options: {
        columnWidths: [
          { wch: 12 }, // Application ID
          { wch: 20 }, // Name
          { wch: 25 }, // Email
          { wch: 15 }, // Phone
          { wch: 15 }, // Status
          { wch: 30 }, // Expertise
          { wch: 10 }, // Years
          { wch: 20 }, // Education
          { wch: 30 }, // Certifications
          { wch: 12 }, // Submitted
          { wch: 12 }, // Reviewed
          { wch: 12 }, // Approved
          { wch: 12 }, // Rejected
          { wch: 30 }, // Rejection Reason
          { wch: 50 }, // Bio
        ],
      },
    });
  }

  // Add cadre members sheet if data exists
  if (data.cadreMembers && data.cadreMembers.length > 0) {
    sheets.push({
      data: formatCadreMembersForExport(data.cadreMembers),
      sheetName: "Cadre Members",
      options: {
        columnWidths: [
          { wch: 10 }, // Member ID
          { wch: 20 }, // Name
          { wch: 25 }, // Email
          { wch: 15 }, // Phone
          { wch: 10 }, // Status
          { wch: 30 }, // Expertise
          { wch: 10 }, // Years
          { wch: 20 }, // Education
          { wch: 30 }, // Certifications
          { wch: 8 }, // Rating
          { wch: 10 }, // Panels Completed
          { wch: 12 }, // Availability
          { wch: 15 }, // Region
          { wch: 12 }, // Is Panel Admin
          { wch: 12 }, // Admin For Panels
          { wch: 12 }, // Joined Date
          { wch: 12 }, // COI Status
        ],
      },
    });
  }

  // Add summary sheet
  const summaryData = [
    {
      "Report Generated": new Date().toLocaleString(),
      "Total Panels": data.panels?.length || 0,
      "Active Panels":
        data.panels?.filter((p) => p.status === "IN_PROGRESS").length || 0,
      "Completed Panels":
        data.panels?.filter((p) => p.status === "COMPLETED").length || 0,
      "Published Panels":
        data.panels?.filter((p) => p.status === "PUBLISHED").length || 0,
      "Total Applications": data.applications?.length || 0,
      "Pending Applications":
        data.applications?.filter(
          (a) => a.status === "PENDING" || a.status === "IN_REVIEW"
        ).length || 0,
      "Approved Applications":
        data.applications?.filter((a) => a.status === "APPROVED").length || 0,
      "Total Cadre Members": data.cadreMembers?.length || 0,
      "Active Cadre Members":
        data.cadreMembers?.filter((m) => m.availability === "AVAILABLE")
          .length || 0,
    },
  ];

  sheets.unshift({
    data: summaryData,
    sheetName: "Summary",
    options: {
      columnWidths: Array(10).fill({ wch: 20 }),
    },
  });

  return exportMultipleSheets(sheets, "APEX_Admin_Report");
};

const excelExportUtils = {
  exportToExcel,
  formatPanelsForExport,
  formatApplicationsForExport,
  formatCadreMembersForExport,
  exportMultipleSheets,
  exportAdminReport,
};

export default excelExportUtils;
